/**
 * Live store (do not add a second database):
 * Articles are KnowledgeArticle rows from GET/POST/PATCH /api/knowledge/articles.
 * collectionKeys are optional. Standalone articles omit them.
 *
 * One UI names that same data:
 * - Knowledge base: one named workspace inside the org. An org starts with none and
 *   may have many after New knowledge base. Membership uses a reserved collection key
 *   `kb:<id>` (see knowledgeBaseRegistry.ts). Switch the active base in the Knowledge
 *   chrome; Libraries, Books, and Articles are scoped to it.
 * - Articles: the list for the active knowledge base. Create from here. A book is not required.
 * - Library: optional group of books inside the active knowledge base. Create via
 *   New library (client registry in knowledgeLibraryRegistry.ts). Books may reference
 *   a library id from that registry; unassigned books appear under Books library.
 *   An org starts with no libraries until you create one.
 * - Book: optional collectionKey. An article is in a book when collectionKeys includes that key.
 * - Article: one KnowledgeArticle. The body field is markdown.
 *
 * Custom books created in One (New book) are stored in an org + knowledge-base scoped
 * client registry (see knowledgeBookRegistry.ts) so empty books appear in the Books list
 * before any article uses the key. The monolith has no collections or knowledge-bases API;
 * article keys still work. New knowledge base also creates a draft anchor article tagged
 * with `kb:<id>` so empty bases can be rediscovered from the articles API.
 *
 * Saves for a selected base always include the active `kb:<id>` membership key.
 * Rename updates the local registry name (id unchanged). Delete confirms, strips
 * that base's `kb:<id>` membership from articles (bodies and book keys stay),
 * clears this base's custom-book and custom-library registries, and removes the base from the shared
 * localStorage list used by One and Agents.
 * Nothing here migrates production rows. Nothing is auto-seeded for a new org.
 */

export type KnowledgeArticleStatus = "draft" | "published";

export type KnowledgeArticle = {
  id: string;
  title: string;
  markdown: string;
  collectionKeys: string[];
  status: KnowledgeArticleStatus;
  updatedAt: string;
  createdAt?: string;
};

/** Legacy suite library id; UI no longer auto-lists it. */
export const SUITE_LIBRARY_ID = "suite";
export const OTHER_LIBRARY_ID = "other";

export type KnowledgeLibraryId = typeof SUITE_LIBRARY_ID | typeof OTHER_LIBRARY_ID;

/** Kept for call-site compatibility; Suite presets are never injected. */
export type KnowledgeBookPreset = {
  collectionKey: string;
  title: string;
  description: string;
};

/** Deprecated: orgs start with zero books. Always empty. */
export function listSuiteBookPresets(): KnowledgeBookPreset[] {
  return [];
}

export function isKnowledgeLibraryId(value: string): value is KnowledgeLibraryId {
  return value === SUITE_LIBRARY_ID || value === OTHER_LIBRARY_ID;
}

import {
  knowledgeArticleEditPath as knowledgeArticleEditPathForBase,
  knowledgeArticlePath as knowledgeArticlePathForBase,
  knowledgeBookPath as knowledgeBookPathForBase,
  knowledgeLibraryPath as knowledgeLibraryPathForBase,
  knowledgePathAfterBaseSwitch,
} from "@/lib/knowledgePaths";

export function bookPath(baseId: string, collectionKey: string): string {
  return knowledgeBookPathForBase(baseId, collectionKey);
}

export function libraryPath(baseId: string, libraryId: string): string {
  return knowledgeLibraryPathForBase(baseId, libraryId);
}

/** List route to use when the active knowledge base changes on a detail page. */
export function knowledgeListPathAfterBaseSwitch(pathname: string, nextBaseId: string): string | null {
  return knowledgePathAfterBaseSwitch(pathname, nextBaseId);
}

export function articlePath(baseId: string, articleId: string): string {
  return knowledgeArticlePathForBase(baseId, articleId);
}

export function articleEditPath(baseId: string, articleId: string): string {
  return knowledgeArticleEditPathForBase(baseId, articleId);
}

export function formatKnowledgeDate(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString();
}

export function knowledgeStatusLabel(status: string): string {
  return status === "published" ? "Published" : "Draft";
}
